import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, RefreshCwIcon, InfoIcon, MailIcon, ExternalLinkIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { emailNotificationService, EmailConfig } from '../../utils/emailNotifications';
export function EmailNotificationsConfig() {
  const [config, setConfig] = useState<EmailConfig>({
    publicKey: '',
    serviceId: '',
    templateId: '',
    enabled: false,
    senderName: 'Savidna'
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  // Template variables reference
  const templateVars = [{
    name: 'to_email',
    desc: 'Correo del paciente'
  }, {
    name: 'to_name',
    desc: 'Nombre del paciente'
  }, {
    name: 'from_name',
    desc: 'Nombre del remitente (clínica)'
  }, {
    name: 'subject',
    desc: 'Asunto del correo'
  }, {
    name: 'message',
    desc: 'Mensaje completo formateado'
  }, {
    name: 'patient_name',
    desc: 'Nombre del paciente'
  }, {
    name: 'doctor_name',
    desc: 'Nombre del doctor'
  }, {
    name: 'service_name',
    desc: 'Nombre del servicio'
  }, {
    name: 'date',
    desc: 'Fecha de la cita'
  }, {
    name: 'time',
    desc: 'Hora de la cita'
  }, {
    name: 'duration',
    desc: 'Duración'
  }, {
    name: 'action_type',
    desc: 'Tipo de acción (Confirmada, Cancelada, etc.)'
  }];
  const [copiedVar, setCopiedVar] = useState<string | null>(null);
  useEffect(() => {
    const savedConfig = emailNotificationService.getConfig();
    if (savedConfig) {
      setConfig(savedConfig);
    }
  }, []);
  const handleSave = () => {
    emailNotificationService.saveConfig(config);
    setSaveMessage('✅ Configuración guardada correctamente');
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    emailNotificationService.saveConfig(config);
    if (!config.publicKey || !config.serviceId || !config.templateId) {
      setTestResult({
        success: false,
        message: 'Completa todos los campos: Public Key, Service ID y Template ID.'
      });
      setIsTesting(false);
      return;
    }
    setTestResult({
      success: true,
      message: 'Configuración válida. Los correos se enviarán automáticamente al gestionar citas.'
    });
    setIsTesting(false);
  };
  const handleCopyVar = (varName: string) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(null), 1500);
  };
  const isConfigComplete = config.publicKey && config.serviceId && config.templateId;
  return <div className="space-y-6" data-id="element-1440">
      <div data-id="element-1441">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1442">
          <MailIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" data-id="element-1443" />
          Notificaciones por Correo (EmailJS)
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1444">
          Envía correos reales a tus pacientes directamente desde el navegador
        </p>
      </div>

      {/* Status Card */}
      <Card className={`${config.enabled && isConfigComplete ? 'bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/30 dark:to-cyan-950/30 border-teal-200 dark:border-teal-800' : 'bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/30 dark:to-slate-800/30 border-gray-200 dark:border-gray-700'}`} data-id="element-1445">
        <div className="flex items-center justify-between" data-id="element-1446">
          <div className="flex items-center gap-4" data-id="element-1447">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${config.enabled && isConfigComplete ? 'bg-teal-500 dark:bg-teal-600 shadow-teal-500/20' : 'bg-gray-400 dark:bg-gray-600 shadow-gray-500/20'}`} data-id="element-1448">
              <MailIcon className="w-7 h-7 text-white" data-id="element-1449" />
            </div>
            <div data-id="element-1450">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1451">
                {config.enabled && isConfigComplete ? '✅ Notificaciones Activas' : 'Notificaciones Inactivas'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1452">
                {config.enabled && isConfigComplete ? 'Los correos se enviarán automáticamente al crear, cancelar o reprogramar citas' : 'Configura EmailJS para activar los correos automáticos'}
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer" data-id="element-1453">
            <input type="checkbox" checked={config.enabled} onChange={e => setConfig({
            ...config,
            enabled: e.target.checked
          })} className="sr-only peer" data-id="element-1454" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-teal-500" data-id="element-1455"></div>
          </label>
        </div>
      </Card>

      {saveMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm" data-id="element-1456">
          {saveMessage}
        </div>}

      {/* EmailJS Configuration */}
      <Card data-id="element-1457">
        <div className="space-y-4" data-id="element-1458">
          <div className="flex items-center justify-between" data-id="element-1459">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-1460">
              Credenciales de EmailJS
            </h3>
            <a href="https://dashboard.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-sm text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1" data-id="element-1461">
              Abrir Dashboard
              <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-1462" />
            </a>
          </div>

          <Input label="Public Key" value={config.publicKey} onChange={e => setConfig({
          ...config,
          publicKey: e.target.value
        })} placeholder="Ej: aBcDeFgHiJkLmN" helperText="Dashboard → Account → Public Key" data-id="element-1463" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1464">
            <Input label="Service ID" value={config.serviceId} onChange={e => setConfig({
            ...config,
            serviceId: e.target.value
          })} placeholder="Ej: service_abc123" helperText="Dashboard → Email Services → Service ID" data-id="element-1465" />
            <Input label="Template ID" value={config.templateId} onChange={e => setConfig({
            ...config,
            templateId: e.target.value
          })} placeholder="Ej: template_xyz789" helperText="Dashboard → Email Templates → Template ID" data-id="element-1466" />
          </div>

          <Input label="Nombre del Remitente" value={config.senderName} onChange={e => setConfig({
          ...config,
          senderName: e.target.value
        })} placeholder="Savidna - Centro de Bienestar" data-id="element-1467" />

          <div className="flex items-center gap-3 pt-4 flex-wrap" data-id="element-1468">
            <Button onClick={handleSave} data-id="element-1469">Guardar</Button>
            <Button variant="secondary" onClick={handleTestConnection} isLoading={isTesting} disabled={!isConfigComplete} data-id="element-1470">
              <RefreshCwIcon className="w-4 h-4" data-id="element-1471" />
              Verificar
            </Button>
            <Button variant="secondary" onClick={() => setShowInstructions(!showInstructions)} data-id="element-1472">
              <InfoIcon className="w-4 h-4" data-id="element-1473" />
              {showInstructions ? 'Ocultar' : 'Ver'} Instrucciones
            </Button>
          </div>

          {testResult && <div className={`p-3 rounded-lg border flex items-start gap-2 ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'}`} data-id="element-1474">
              {testResult.success ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1475" /> : <XCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-1476" />}
              <p className="font-medium whitespace-pre-line" data-id="element-1477">
                {testResult.message}
              </p>
            </div>}
        </div>
      </Card>

      {/* Instructions */}
      {showInstructions && <Card data-id="element-1478">
          <div className="space-y-6" data-id="element-1479">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-1480">
              Configuración en 4 Pasos
            </h3>

            <div className="space-y-6" data-id="element-1481">
              {/* Step 1 */}
              <div className="flex gap-4" data-id="element-1482">
                <div className="flex-shrink-0" data-id="element-1483">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center" data-id="element-1484">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-lg" data-id="element-1485">
                      1
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-1486">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1487">
                    Crea una cuenta en EmailJS
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1488">
                    Ve a{' '}
                    <a href="https://www.emailjs.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline" data-id="element-1489">
                      emailjs.com
                    </a>{' '}
                    y regístrate gratis (200 correos/mes gratis).
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4" data-id="element-1490">
                <div className="flex-shrink-0" data-id="element-1491">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center" data-id="element-1492">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-lg" data-id="element-1493">
                      2
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-1494">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1495">
                    Conecta tu servicio de email
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1496">
                    Dashboard → Email Services → Add New Service → Conecta
                    Gmail, Outlook, etc. Copia el <strong data-id="element-1497">Service ID</strong>.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4" data-id="element-1498">
                <div className="flex-shrink-0" data-id="element-1499">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center" data-id="element-1500">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-lg" data-id="element-1501">
                      3
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-1502">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1503">
                    Crea una plantilla de email
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-1504">
                    Dashboard → Email Templates → Create New Template. Copia el{' '}
                    <strong data-id="element-1505">Template ID</strong>. Usa estas variables en tu
                    plantilla:
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-1506">
                    <div className="grid grid-cols-1 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1507">
                      {templateVars.map(v => <div key={v.name} className="flex items-center justify-between px-3 py-2" data-id="element-1508">
                          <div className="flex items-center gap-2" data-id="element-1509">
                            <code className="text-xs bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 px-1.5 py-0.5 rounded font-mono" data-id="element-1510">
                              {`{{${v.name}}}`}
                            </code>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1511">
                              {v.desc}
                            </span>
                          </div>
                          <button onClick={() => handleCopyVar(v.name)} className="p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 rounded transition-colors" title="Copiar" data-id="element-1512">
                            {copiedVar === v.name ? <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1513" /> : <CopyIcon className="w-3.5 h-3.5" data-id="element-1514" />}
                          </button>
                        </div>)}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-1515">
                    💡 La variable más importante es{' '}
                    <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded" data-id="element-1516">
                      {'{{message}}'}
                    </code>{' '}
                    que contiene todo el mensaje formateado.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4" data-id="element-1517">
                <div className="flex-shrink-0" data-id="element-1518">
                  <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center" data-id="element-1519">
                    <span className="text-teal-600 dark:text-teal-400 font-bold text-lg" data-id="element-1520">
                      4
                    </span>
                  </div>
                </div>
                <div className="flex-1" data-id="element-1521">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1522">
                    Copia tu Public Key
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1523">
                    Dashboard → Account → Public Key. Pégala arriba junto con el
                    Service ID y Template ID.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4" data-id="element-1524">
              <p className="text-sm text-teal-700 dark:text-teal-300" data-id="element-1525">
                <strong data-id="element-1526">✅ ¡Listo!</strong> Con EmailJS los correos se envían
                directamente desde el navegador. No necesitas servidor ni Google
                Apps Script.
              </p>
            </div>
          </div>
        </Card>}
    </div>;
}